import React, { useState, useEffect } from 'react';
import { XMarkIcon, CurrencyDollarIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { usersApi } from '../../services/api';
import { CreditHistory as CreditHistoryType } from '../../types';

interface CreditHistoryProps {
  userId: number;
  userName: string;
  onClose: () => void;
}

const CreditHistory: React.FC<CreditHistoryProps> = ({ userId, userName, onClose }) => {
  const [history, setHistory] = useState<CreditHistoryType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  useEffect(() => {
    loadCreditHistory();
  }, [userId, limit, currentPage]);

  const loadCreditHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const skip = (currentPage - 1) * limit;
      const response = await usersApi.getUserCreditHistory(userId, limit, skip);
      
      // Handle both old format (array) and new format (object with data and meta)
      if (Array.isArray(response.data)) {
        // Old format - just an array
        setHistory(response.data);
        setTotalPages(1);
        setTotalEntries(response.data.length);
        setHasNext(false);
        setHasPrev(false);
      } else {
        // New format - object with data and meta
        setHistory(response.data.data || []);
        setTotalPages(response.data.meta?.pages || 1);
        setTotalEntries(response.data.meta?.total || 0);
        setHasNext(response.data.meta?.has_next || false);
        setHasPrev(response.data.meta?.has_prev || false);
      }
    } catch (err: any) {
      console.error('Failed to load credit history:', err);
      setError(err.response?.data?.detail || 'Failed to load credit history');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const getOperationIcon = (operationType: string) => {
    switch (operationType) {
      case 'deduct':
        return <ArrowDownIcon className="h-4 w-4 text-red-500" />;
      case 'add':
      case 'initialize':
        return <ArrowUpIcon className="h-4 w-4 text-green-500" />;
      case 'reset':
        return <CurrencyDollarIcon className="h-4 w-4 text-blue-500" />;
      default:
        return <CurrencyDollarIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getOperationColor = (operationType: string) => {
    switch (operationType) {
      case 'deduct':
        return 'text-red-600 dark:text-red-400';
      case 'add':
      case 'initialize':
        return 'text-green-600 dark:text-green-400';
      case 'reset':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatAmount = (amount: number, operationType: string) => {
    const sign = operationType === 'deduct' ? '-' : '+';
    return `${sign}${Math.abs(amount).toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const isHistoricalEntry = (description: string) => {
    return description.includes('task date:') || description.includes('Historical:');
  };

  const extractTaskDate = (description: string) => {
    const match = description.match(/task date: (\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-light-card dark:bg-dark-card rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center gap-3">
            <CurrencyDollarIcon className="h-6 w-6 text-primary-500" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Credit History
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {userName} (User ID: {userId})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Show:</label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="px-3 py-1 rounded-lg bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={25}>25 entries</option>
                <option value={50}>50 entries</option>
                <option value={100}>100 entries</option>
                <option value={200}>200 entries</option>
              </select>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)] custom-scrollbar">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-light-surface dark:bg-dark-surface">
                    <div className="w-8 h-8 bg-light-hover dark:bg-dark-hover rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-light-hover dark:bg-dark-hover rounded w-3/4"></div>
                      <div className="h-3 bg-light-hover dark:bg-dark-hover rounded w-1/2"></div>
                    </div>
                    <div className="w-20 h-4 bg-light-hover dark:bg-dark-hover rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-500 dark:text-red-400 mb-2">
                <CurrencyDollarIcon className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Failed to Load Credit History
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
              <button
                onClick={loadCreditHistory}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <CurrencyDollarIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Credit History
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                This user has no credit transaction history yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {history
                .filter((entry) => !entry.description || entry.description.includes('User:'))
                .map((entry) => {
                const isHistorical = isHistoricalEntry(entry.description);
                const taskDate = extractTaskDate(entry.description);
                
                return (
                  <div
                    key={entry.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      isHistorical
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                        : 'bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border hover:bg-light-hover dark:hover:bg-dark-hover'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Operation Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getOperationIcon(entry.operation_type)}
                      </div>

                      {/* Main Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            {/* Operation Type and Amount */}
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                {entry.operation_type}
                              </span>
                              <span className={`text-sm font-semibold ${getOperationColor(entry.operation_type)}`}>
                                {formatAmount(entry.amount, entry.operation_type)} credits
                              </span>
                              {isHistorical && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                                  Historical
                                </span>
                              )}
                            </div>

                            {/* Description */}
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              {entry.description}
                            </p>

                            {/* Historical Task Date */}
                            {isHistorical && taskDate && (
                              <div className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                                Original Task Date: {taskDate}
                              </div>
                            )}

                            {/* Balance Change */}
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Balance: {entry.balance_before.toLocaleString()} → {entry.balance_after.toLocaleString()} credits
                            </div>

                            {/* Reference ID */}
                            {entry.reference_id && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Ref: {entry.reference_id}
                              </div>
                            )}
                          </div>

                          {/* Timestamp */}
                          <div className="flex-shrink-0 text-right">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(entry.created_at)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !error && (
          <div className="px-6 py-4 border-t border-light-border dark:border-dark-border bg-light-surface dark:bg-dark-surface">
            <div className="flex items-center justify-between">
              {/* Left side - Entry info and legend */}
              <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                <div>
                  {(() => {
                    const filteredHistory = history.filter((entry) => !entry.description || entry.description.includes('User:'));
                    const filteredCount = filteredHistory.length;
                    if (filteredCount > 0) {
                      return `Showing ${filteredCount} user entries (filtered from ${totalEntries.toLocaleString()} total)`;
                    } else {
                      return 'No user entries found';
                    }
                  })()}
                </div>
                {history.length > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
                      <span>Historical</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-light-border dark:bg-dark-border rounded-full"></div>
                      <span>Regular</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Right side - Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!hasPrev}
                    className="px-3 py-1 rounded-lg border border-light-border dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {/* Show page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            pageNum === currentPage
                              ? 'bg-primary-500 text-white'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNext}
                    className="px-3 py-1 rounded-lg border border-light-border dark:border-dark-border text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditHistory;
